import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Pill, Check, Trash2 } from "lucide-react";
import { PageHeader } from "./Sugar";

export default function Medication() {
  const [meds, setMeds] = useState([]);
  const [logs, setLogs] = useState([]);
  const [form, setForm] = useState({ name: "", dosage: "", frequency: "Once daily", start_date: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const [a, b] = await Promise.all([api.get("/medication"), api.get("/medication/log")]);
      setMeds(a.data); setLogs(b.data);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      const payload = { ...form, start_date: form.start_date || new Date().toISOString().slice(0, 10) };
      await api.post("/medication", payload);
      setForm({ name: "", dosage: "", frequency: "Once daily", start_date: "" });
      await load();
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  const markTaken = async (id) => { await api.post("/medication/log", { medication_id: id, status: "taken" }); await load(); };
  const del = async (id) => { await api.delete(`/medication/${id}`); await load(); };

  const takenToday = (medId) => {
    const start = new Date(); start.setHours(0, 0, 0, 0);
    return logs.find(l => l.medication_id === medId && new Date(l.taken_at) >= start);
  };

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto" data-testid="medication-page">
      <PageHeader icon={Pill} title="Medication" subtitle="Manage doses and routines" accent="emerald" />

      <form onSubmit={add} className="bg-white border border-slate-200 rounded-2xl p-5 mb-6 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="med-form">
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Name</Label>
          <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-xl bg-slate-50" data-testid="med-name" placeholder="e.g. Metformin" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Dosage</Label>
          <Input required value={form.dosage} onChange={(e) => setForm({ ...form, dosage: e.target.value })} className="mt-1 rounded-xl bg-slate-50" data-testid="med-dosage" placeholder="500 mg" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Frequency</Label>
          <Input required value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })} className="mt-1 rounded-xl bg-slate-50" data-testid="med-frequency" placeholder="Twice daily" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Start date</Label>
          <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1 rounded-xl bg-slate-50" data-testid="med-start" />
        </div>
        {error && <div className="sm:col-span-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">{error}</div>}
        <div className="sm:col-span-2 flex justify-end">
          <Button type="submit" disabled={loading} className="h-11 px-6 rounded-full bg-blue-600 hover:bg-blue-700" data-testid="med-submit">{loading ? "Saving…" : "Add medication"}</Button>
        </div>
      </form>

      <h3 className="font-heading font-semibold text-slate-800 mb-3">Your medications</h3>
      <div className="space-y-2" data-testid="med-list">
        {meds.length === 0 && <div className="text-sm text-slate-400">No medications added.</div>}
        {meds.map(m => {
          const taken = takenToday(m.id);
          return (
            <div key={m.id} className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-slate-900">{m.name} <span className="text-slate-400 font-normal">• {m.dosage}</span></div>
                <div className="text-xs text-slate-500">{m.frequency}{m.start_date ? ` • since ${m.start_date}` : ""}</div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => markTaken(m.id)}
                  data-testid={`med-take-${m.id}`}
                  variant={taken ? "outline" : "default"}
                  className={`rounded-full h-9 px-4 ${taken ? "border-emerald-200 text-emerald-700 bg-emerald-50 hover:bg-emerald-50" : "bg-blue-600 hover:bg-blue-700"}`}
                >
                  <Check className="w-4 h-4 mr-1" /> {taken ? "Taken today" : "Mark taken"}
                </Button>
                <button onClick={() => del(m.id)} data-testid={`med-del-${m.id}`} className="text-slate-400 hover:text-rose-500 p-1"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          );
        })}
      </div>

      <h3 className="font-heading font-semibold text-slate-800 mb-3 mt-8">Recent doses</h3>
      <div className="space-y-2">
        {logs.slice(0, 8).map(l => {
          const m = meds.find(x => x.id === l.medication_id);
          return (
            <div key={l.id} className="bg-white border border-slate-200 rounded-xl px-4 py-2 flex items-center justify-between text-sm">
              <span className="text-slate-700">{m?.name || "Medication"} <span className="text-slate-400">• {m?.dosage}</span></span>
              <span className="text-xs text-slate-500">{new Date(l.taken_at).toLocaleString()}</span>
            </div>
          );
        })}
        {logs.length === 0 && <div className="text-sm text-slate-400">No dose history yet.</div>}
      </div>
    </div>
  );
}
